import Column from '@/Components/Grid/Column';
import Container from '@/Components/Grid/Container';
import Row from '@/Components/Grid/Row';
import { PageProps } from '@/types';
import { Head, Link } from '@inertiajs/react';
import { useEffect } from 'react';

import HeroImageWebp from '@static/splash-patient.png?w=3600;2560;1920;1500;991&format=webp&quality=85&as=srcset&imagetools';

import HeadingWithDecor from '@/Components/HeadingWithDecor';
import AccordionItem from '@/Components/Pages/Splash/AccordionItem';
import PatientDocCard from '@/Components/Patient/PatientDocCard';
import PatientPostItem from '@/Components/Patient/PatientPostItem';
import Advertise from '@/Components/Platform/Advertise';
import { ArticleModel } from '@/types/models/ArticleModel';
import { DocumentModel } from '@/types/models/DocumentModel';
import AdvertiseBanner from "@static/ads/inauguration.jpg?quality=85&format=webp&imagetools";
import { VideoModel } from '@/types/models/VideoModel';
import { PodcastSeriesModel } from '@/types/models/PodcastSeriesModel';
import PatientMovieBlock from '@/Components/Patient/PatientMovieBlock';
import PatientPodcastBlock from '@/Components/Patient/PatientPodcastBlock';
import PatientLayout from '@/Layouts/PatientLayout';
import LatestMaterial from '@/Components/Patient/LatestMaterial';
import LatestMaterialSlider from '@/Components/Patient/LatestMaterialSlider';
import { TherapeuticAreaModel } from '@/types/models/TherapeuticAreaModel';

import HeroOncology from "@images/patient/onkologia.jpg?quality=85&format=webp&imagetools";
import HeroNeurology from "@images/patient/neurologia.jpg?quality=85&format=webp&imagetools";
import HeroPsychology from "@images/patient/psychiatria.jpg?quality=85&format=webp&imagetools";
import HeroUrology from "@images/patient/urologia.jpg?quality=85&format=webp&imagetools";
import HeroOsteoporosis from "@images/patient/osteoporoza.jpg?quality=85&format=webp&imagetools";
import HeroReumatology from "@images/patient/reumatologia.jpg?quality=85&format=webp&imagetools";
import OsteoporosisLogo from "@images/accord-logo-osteoporosis.svg";

const Index = ({ category, therapeuticArea, articles, docs, videos, podcastSeries, latestRecords }: PageProps<{
    category: string
    therapeuticArea: TherapeuticAreaModel
    articles: ArticleModel[],
    docs: DocumentModel[],
    videos: VideoModel[],
    podcastSeries: PodcastSeriesModel[],
    latestRecords: any[]
}>) => {

    const formatDate = (dateString: string) => {
        return dateString.split(' ')[0];
    };

    useEffect(() => {
        const scrollToHash = () => {
            const hash = window.location.hash.slice(1);
            if (!hash) return;
            document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth' });
        };

        const timeout = setTimeout(scrollToHash, 100);
        return () => clearTimeout(timeout);
    }, [category]);

    const NoResults = ({ section }: { section: string }) => (
        <p className="text-arcticMist text-center w-full">Brak {section} dla kategorii {therapeuticArea.name}</p>
    );

    const ResolvedCategoryLogo = () => {
        switch (category) {
            case 'onkologia':
                return <img src="/storage/static/accord-logo-oncology.svg" width={201} className="pb-16" alt={`Accord Academy ${category}`} />
            case 'hematologia':
                return <img src="/storage/static/accord-logo-hematology.svg" width={201} className="pb-16" alt={`Accord Academy ${category}`} />
            case 'reumatologia':
                return <img src="/storage/static/accord-logo-reumatology.svg" width={201} className="pb-16" alt={`Accord Academy ${category}`} />
            case 'neurologia':
                return <img src="/storage/static/accord-logo-neurology.svg" width={201} className="pb-16" alt={`Accord Academy ${category}`} />
            case 'psychiatria':
                return <img src="/storage/static/accord-logo-psychology.svg" width={201} className="pb-16" alt={`Accord Academy ${category}`} />
            case 'urologia':
                return <img src="/storage/static/accord-logo-urology.svg" width={201} className="pb-16" alt={`Accord Academy ${category}`} />
            case 'osteoporoza':
                return <img src={OsteoporosisLogo} width={201} className="pb-16" alt={`Accord Academy ${category}`} />
            case 'default':
                return <img src="/storage/static/accord-logo-urology.svg" width={201} className="pb-16" alt={`Accord Academy ${category}`} />
        }
    }

    const heroImage = {
        'onkologia': HeroOncology,
        'neurologia': HeroNeurology,
        'psychiatria': HeroPsychology,
        'urologia': HeroUrology,
        'osteoporoza': HeroOsteoporosis,
        'reumatologia': HeroReumatology,
    }[category];

    return (
        <PatientLayout category={category}>
            <Head title={`Przeglądaj materiały terapeutyczne w dziedzinie ${category}`} />

            <aside className="lg:mb-16 mb-8">
                <img src={heroImage} alt={category} className="w-full h-full object-cover object-center" />
            </aside>

            <section className='lg:mb-16 mb-8'>
                <Container>
                    <Row>
                        <Column>
                            <ResolvedCategoryLogo />
                        </Column>
                    </Row>
                </Container>
            </section>

            <section className='lg:mb-16 mb-8'>
                <Container>
                    <Row className='mb-6'>
                        <Column className='text-center'>
                            <HeadingWithDecor as='h2'>
                                Ostatnio dodane materiały
                            </HeadingWithDecor>
                        </Column>
                    </Row>
                    {latestRecords?.length ? (
                        <LatestMaterialSlider>
                            {latestRecords.map((d, i) => (
                                // @ts-ignore
                                <LatestMaterial
                                    key={i}
                                    model={d}
                                />
                            ))}
                        </LatestMaterialSlider>
                    ) : (
                        <NoResults section="ostatnio dodanych materiałów" />
                    )}
                </Container>
            </section>

            <section className='lg:mb-32 mb-8' id='dokumenty-do-pobrania'>
                <Container>
                    <Row className='gap-y-6'>
                        <Column className='text-center mb-26 maxMd:mb-12'>
                            <h3 className='text-arcticMist h2 font-semibold'>Materiały do pobrania</h3>
                        </Column>
                        {docs?.length ? docs.map((d, i) => (
                            <Column xl={3} md={6} lg={4} key={i}>
                                <PatientDocCard
                                    hero={d.hero ? '/patient' + d.hero + '/' + d.id + '/document' : ''}
                                    file={d.file}
                                    title={d.name}
                                    id={d.id}
                                    category={d.therapeutic_areas[0].name}
                                    slug={d.slug}
                                    created_at={d.created_at}
                                    is_pin_protected={Boolean((d as any).is_pin_protected)}
                                />
                            </Column>
                        )) : (
                            <Column>
                                <NoResults section="materiałów do pobrania" />
                            </Column>
                        )}
                    </Row>
                </Container>
            </section>

            <section className='lg:mb-16 mb-8' id='artykuly'>
                <Container>
                    <Row>
                        <Column className='text-center mb-26 maxMd:mb-12'>
                            <h3 className='text-arcticMist h2 font-semibold'>Artykuły dla pacjenta</h3>
                        </Column>
                        <Column xl={10} className='mx-auto'>
                            <div className="px-16 maxMd:px-0">
                                <Row>
                                    <Column>
                                        {articles?.length ? articles.map((article, i) => (
                                            <PatientPostItem key={i} {...article} />
                                        )) : (
                                            <NoResults section="artykułów" />
                                        )}
                                    </Column>
                                </Row>
                            </div>
                        </Column>
                    </Row>
                </Container>
            </section>

            <section className='lg:mb-32 mb-8' id='materialy-video'>
                <Container>
                    <Row>
                        <Column className='text-center mb-26 maxMd:mb-12'>
                            <h3 className='text-arcticMist h2 font-semibold'>Materiały video dla pacjenta</h3>
                        </Column>
                        <Column xl={10} className='mx-auto'>
                            <div className="px-16 maxMd:px-0">
                                <Row className='gap-y-6'>
                                    {videos?.length ? videos.map((video, i) => (
                                        <Column key={i} ultraLarge={3} xl={4} lg={4} md={6}>
                                            <PatientMovieBlock
                                                image={'/patient' + video.hero + '/' + video.id + '/video'}
                                                timeSummary={video.time}
                                                title={video.name}
                                                link={route('front.patient.movies.single.player', { video: video })}
                                                model={video}
                                                is_pin_protected={Boolean((video as any).is_pin_protected)}
                                            />
                                        </Column>
                                    )) : (
                                        <Column>
                                            <NoResults section="materiałów wideo" />
                                        </Column>
                                    )}
                                </Row>
                            </div>
                        </Column>
                    </Row>
                </Container>
            </section>

            <section className='lg:mb-32 mb-8' id='podcasty'>
                <Container>
                    <Row>
                        <Column className='text-center mb-26 maxMd:mb-12'>
                            <h3 className='text-arcticMist h2 font-semibold'>Podcasty dla pacjenta</h3>
                        </Column>
                        <Column xl={10} className='mx-auto'>
                            <div className="px-16 maxMd:px-0">
                                <Row className='gap-y-6'>
                                    {podcastSeries?.length ? podcastSeries.map((podcast, i) => (
                                        <Column key={i} ultraLarge={2} xl={3} lg={4} md={6}>
                                            <PatientPodcastBlock
                                                image={'/patient' + podcast.podcasts[0].hero + '/' + podcast.id + '/podcastSeries'}
                                                createdAt={formatDate(podcast.created_at)}
                                                title={podcast.name}
                                                link={route('front.patient.podcasts.single', { podcastSeries: podcast, podcast: podcast.podcasts[0]?.slug })}
                                                is_pin_protected={Boolean((podcast as any).is_pin_protected)}
                                            />
                                        </Column>
                                    )) : (
                                        <Column>
                                            <NoResults section="podcastów" />
                                        </Column>
                                    )}
                                </Row>
                            </div>
                        </Column>
                    </Row>
                </Container>
            </section>

            <div className="bg-deepIndigo/70 py-24 maxMd:py-12">
                <Container>
                    <Row className='justify-center'>
                        <Column xl={8} className='text-center'>
                            <h6 className='h2 text-arcticMist font-semibold mb-14 maxMd:mb-10'>Najczęściej zadawane pytania</h6>
                        </Column>
                        <Column xl={8}>
                            <AccordionItem heading='Czym jest Accord Academy?' content='Accord Academy to serwis edukacyjny zrzeszający pacjentów z całego kraju, borykających się z różnymi problemami zdrowotnymi. Serwis poprzez liczne artykuły bądź pliki multimedialne zapewnia pacjentom stały dostęp do informacji. Serwis pozwala pacjentom w szybki i prosty sposób pogłębić wiedzę na interesujące ich tematy medyczne.' />
                            <AccordionItem heading='Czy Accord Academy jest bezpłatne w całości?' content='Tak, serwis jest całkowicie bezpłatny.' />
                            <AccordionItem heading='Jakie treści są publikowane w Accord Academy?' content='W serwisie AccordAcademy, publikujemy treści dostępne dla pacjentów w formie materiałów do czytania lub nagrań wideo.' />
                        </Column>
                    </Row>
                </Container>
            </div>
        </PatientLayout>
    );
}

export default Index;